import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

export const retrieveDistrictNodeConfig = ({
  version = 'v1',
  params,
  nodeId,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/${nodeId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateDistrictNode = ({
  version = 'v1',
  params,
  nodeId,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/${nodeId}/`,
      method: RequestMethod.PATCH,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteDistrictNode = ({
  version = 'v1',
  params,
  nodeId,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/${nodeId}/`,
      method: RequestMethod.DELETE,
      params,
      data: payload,
    })
    .then(
      () => nodeId,
    ) /* Return the nodeId so we can use it in the onSuccess callback */
    .catch(handleError);

export const verifyDistrictNode = ({ version = 'v1', params, nodeId } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/${nodeId}/verify/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const unverifyDistrictNode = ({ version = 'v1', params, nodeId } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/${nodeId}/unverify/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const reorderDistrictNodes = ({
  version = 'v1',
  params,
  parentNodeId,
  newChildrenOrder,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/${parentNodeId}/`,
      method: RequestMethod.PATCH,
      params,
      data: newChildrenOrder,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveTree = ({
  version = 'v1',
  params,
  indicatorTypeUuid,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/indicators/${indicatorTypeUuid}/tree/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createChildDistrictNode = ({
  version = 'v1',
  params,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveIndicatorClassList = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/kri-config/indicator_class_list/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveIndicators = ({ version = 'v1', params, kriUuid } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/indicators/${kriUuid}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
/**
 * Get list of KRI tree from district
 * @param  {String} options.version api version
 * @return {Promise} result is list of entries of Indicator types
 */
export const getKRITreeList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/kri-config/tree-list/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveIndicatorsContext = ({
  version = 'v1',
  params,
  kriUuid,
}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/indicators/${kriUuid}/context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveTreeContext = ({
  version = 'v1',
  params,
  indicatorTypeUuid,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/indicators/${indicatorTypeUuid}/context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveTreeContextTabularView = ({
  version = 'v1',
  params,
  indicatorTypeUuid,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/indicators/${indicatorTypeUuid}/tabular_view_context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveDistrictNodeTree = ({
  version = 'v1',
  params,
  nodeId,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/${nodeId}/tree/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveDistrictNodeRoots = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/roots/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getInitialAutocompleteList = ({
  params,
  version = 'v1',
  apiConfig,
}) =>
  client
    .request({
      url: `${version}/common/autocomplete/`,
      method: RequestMethod.GET,
      params: {
        ...params,
        app_label: apiConfig.appLabel,
        model_name: apiConfig.djangoModelName,
        variant: apiConfig.variant,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateTreeType = ({ version = 'v1', params, data, nodeId } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-tree-config/${nodeId}/`,
      method: RequestMethod.PATCH,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictKriConfig = ({ version = 'v1', params, nodeId } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-tree-config/${nodeId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictKRIConfigByIndicatorType = ({
  version = 'v1',
  params,
  uuid,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-tree-config/by-indicator-type/${uuid}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictKRICustomConfig = ({
  version = 'v1',
  params,
  nodeId,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/kri-custom-config/${nodeId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPointsAchievedByStudent = ({
  version = 'v1',
  params,
  studentId,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/indicators/${studentId}/tree/points/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createTree = ({ version = 'v1', params, payload } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-tree-config/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const copyDistrictNode = ({ version = 'v1', params, payload } = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/district-node/copy/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .catch(handleError);

export const getCompoundIndicatorOptions = ({
  version = 'v1',
  params,
  nodeId,
  rootNodeId,
} = {}) =>
  client
    .request({
      url: `${version}/key-readiness-indicators/kri-config/compound-indicators/${rootNodeId}/${nodeId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
